import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Card, Grid } from "@material-ui/core";
import PublicComponent from "../PublicComponent";
import publicMainStyle from "assets/jss/components/publicMainStyles";
import { makeStyles } from "@material-ui/core/styles";
import { useMutationWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { PUBLIC_CONTRACTUAL_DOCUMENTS_QUERY } from "../../queries";
import { get, sequence, set, useStateLens } from "@totalsoft/react-state-lens";
import { emptyArray, emptyString } from "constants/defaultValues";
import ContractDocumentsContainer from "features/contractStep/components/ContractDocumentsContainer";
import { quoteStatus } from "constants/quoteStatus";
import { useTranslation } from "react-i18next";
import ActionsDialog from "features/common/components/ActionsDialog";
import { CONFIRM_CONTRACTUAL_UPLOADED_FILES } from "../../mutations";

const useStyles = makeStyles(publicMainStyle);

const PublicContractualDocuments = (props) => {
  const { stateLens } = props;
  const quoteId = stateLens.data.quoteId |> get;
  const transactionId = stateLens.data.transactionId |> get;
  const classes = useStyles();
  const publicContractualDocumentsLens = useStateLens(emptyArray);
  const [quoteStatusId, setQuoteStatusId] = useState(null);
  const { t } = useTranslation();
  const [actionsDialogProps, setActionsDialogProps] = useState({
    open: false,
    title: emptyString,
    message: emptyString,
    actions: emptyArray,
  });

  const { data, loading } = useQueryWithErrorHandling(PUBLIC_CONTRACTUAL_DOCUMENTS_QUERY, {
    variables: { quoteId: parseInt(quoteId) },
    onCompleted: (data) => {
      if (!data) return;
      (publicContractualDocumentsLens |> set)(data.publicContractualDocuments.leasingContracts);
      setQuoteStatusId(data.publicContractualDocuments.quoteStatusId);
    },
  });

  const allDocumentsIsUploaded = useMemo(() => {
    const publicContractualDocuments = publicContractualDocumentsLens |> get;
    const test = publicContractualDocuments[0]?.requiredDocuments?.some((x) => x.files.length < 1);
    return test;
  }, [publicContractualDocumentsLens]);

  const handleCloseActionsDialog = useCallback(() => setActionsDialogProps((prev) => ({ ...prev, open: false })), []);

  const disabled = useMemo(() => {
    return quoteStatusId === quoteStatus.documentsUploaded.id;
  }, [quoteStatusId]);

  const [confirmContractualUploadedFiles] = useMutationWithErrorHandling(CONFIRM_CONTRACTUAL_UPLOADED_FILES, {
    onCompleted: (data) => {
      if (!data) return;
      if (data.confirmContractualUploadedFiles === true) setQuoteStatusId(quoteStatus.documentsUploaded.id);
      setActionsDialogProps({
        open: true,
        title:
          data.confirmContractualUploadedFiles === true
            ? t("DocumentsReceivedConfirmation.SignedContractualDocumentsReceived")
            : t("DocumentsReceivedConfirmation.AnErrorOccurred"),
        message:
          data.confirmContractualUploadedFiles === true
            ? t("DocumentsReceivedConfirmation.SignedDocumentReceived")
            : t("DocumentsReceivedConfirmation.ReturnToThisPageAtAnytime"),
        actions: [
          {
            caption: t("General.OK"),
            handle: () => {
              return true;
            },
          },
        ],
      });
    },
  });

  const handleConfirmContractualUploadedFiles = useCallback(() => {
    confirmContractualUploadedFiles({
      variables: {
        quoteId: quoteId,
      },
    });
  }, [confirmContractualUploadedFiles, quoteId]);

  return (
    <Card className={classes.card}>
      <Grid container spacing={3}>
        {(publicContractualDocumentsLens |> get)?.length > 0 &&
          (publicContractualDocumentsLens |> sequence)?.map((leasingContractLens, index) => (
            <Grid item xs={12} key={index}>
              <ContractDocumentsContainer
                quoteId={parseInt(quoteId)}
                transactionId={transactionId}
                leasingContractLens={leasingContractLens}
                quoteStatusId={quoteStatusId}
                isExternal={true}
                disabled={disabled}
              />
            </Grid>
          ))}
        <Grid item xs={12}>
          {!loading && (
            <Button
              className={classes.doneButton}
              variant="contained"
              color="primary"
              onClick={handleConfirmContractualUploadedFiles}
              disabled={allDocumentsIsUploaded || disabled}
            >
              {t("DocumentsReceivedConfirmation.Done")}
            </Button>
          )}
        </Grid>
        <ActionsDialog
          open={actionsDialogProps.open}
          handleClose={handleCloseActionsDialog}
          title={actionsDialogProps.title}
          message={actionsDialogProps.message}
          actions={actionsDialogProps.actions}
          Content={actionsDialogProps.Content}
        />
      </Grid>
    </Card>
  );
};

PublicContractualDocuments.propTypes = {
  stateLens: PropTypes.object.isRequired,
};

export default () => <PublicComponent Component={PublicContractualDocuments} />;
